/**
 * What a pe3d export will name its files, worked out in the browser for the
 * export dialog's Naming tab. The server decides (controller/pe3d/exportNaming.js,
 * version/uploadGame.js `resolveExportName`); this follows the same rules so the
 * preview reads as the result will. Pure, so Jest can run it.
 */

const extensionOf = (name) => (/\.[a-z0-9]{1,5}$/i.exec(String(name)) || [""])[0];

/** A template with its {tokens} filled for one variant. */
export function resolveTemplate(template, { title, language, networks, gameTitle, baseTitle, timeStamp }) {
  const vars = {
    timeStamp: String(timeStamp),
    // The export hands the title over with its spaces already made underscores.
    versionName: String(title || "export").replace(/ /g, "_"),
    languages: String(language || ""),
    adNetworks: (networks || []).join("_"),
    gameTitle: String(gameTitle || "").replace(/ /g, "-"),
    baseTitle: String(baseTitle || "").replace(/ /g, "-"),
    languages_c: String(language || "").toUpperCase(),
    languages_C: String(language || "").toUpperCase(),
  };
  return String(template).replace(/\{(\w+)\}/g, (match, key) => (vars[key] === undefined ? match : vars[key]));
}

/**
 * One variant's file name: by the export's own template when it has one
 * (made safe, with the company template's extension), by the company's otherwise.
 */
export function exportFileName({ own, companyTemplate, variant }) {
  const byCompany = resolveTemplate(companyTemplate, variant);
  if (!String(own || "").trim()) return byCompany;
  const safe = resolveTemplate(String(own).trim(), variant)
    .trim()
    .replace(/\.[a-z0-9]{1,5}$/i, "")
    .replace(/[/\\]/g, "_")
    .replace(/\s+/g, "_");
  return `${safe || "export"}${extensionOf(byCompany) || ".zip"}`;
}

/** Why a typed template cannot be used, or "" when it can. */
export function templateProblem(template, tokens) {
  const typed = String(template || "").trim();
  if (!typed) return "";
  if (typed.length > 200) return "A name template takes at most 200 characters.";
  if (/[/\\]/.test(typed)) return "A name cannot contain / or \\.";
  const unknown = (typed.match(/\{\w+\}/g) || []).filter((token) => !(tokens || []).includes(token.slice(1, -1)));
  return unknown.length ? `${unknown.join(", ")} ${unknown.length === 1 ? "is" : "are"} not a token.` : "";
}

/**
 * The folders of a zip of many, as the export names them: each variant's file
 * name without its extension, and a number on the ones that would be alike.
 */
export function folderNames(fileNames) {
  const taken = new Set();
  return fileNames.map((fileName) => {
    const plain = String(fileName).replace(/\.[a-z0-9]{1,5}$/i, "");
    let name = plain;
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${plain}_${n}`;
    taken.add(name.toLowerCase());
    return name;
  });
}

/** The zip of a many-variant export: the game's title and the typed name. */
export function zipNameOf(gameTitle, typed) {
  const name = [gameTitle, typed]
    .filter((part) => String(part || "").trim())
    .join("_")
    .replace(/[/\\]/g, "_")
    .replace(/^\.+/, "")
    .trim();
  return `${name || "batch"}.zip`;
}
